import { useRef, useId } from 'react';
import PropTypes from 'prop-types';
import useModalA11y from '../../../../hooks/useModalA11y';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';

/**
 * KeyContactsMobileSheet (TD-2485)
 *
 * Mobile bottom-sheet shell for the Key Contacts enrichment/discovery surfaces
 * (Discover + Reveal). A native `<dialog>` opened via `showModal()` through
 * `useModalA11y`, so the platform owns the focus trap, initial focus, Escape,
 * `aria-modal`, top-layer rendering and the `::backdrop` scrim (the rest of the
 * page is inert while it's open). Mirrors the native-`<dialog>` pattern of
 * `FallbackActivationModal`; the bottom-sheet look lives in
 * `key-contacts-mobile-sheet.less` and matches the existing
 * `.cd-mobile-employee-filter-popup` used by the contact filters.
 *
 * The `<dialog>` element itself is a full-viewport, transparent click-to-dismiss
 * surface (no padding); the visible sheet is the inner `.kc-sheet-panel` pinned
 * to the bottom. A click beside/above the panel resolves to the `<dialog>` and
 * `useModalA11y` treats it as a light-dismiss; a click inside the panel does not.
 */
const KeyContactsMobileSheet = ({ isOpen, onClose, title, children, footer = null }) => {
  const dialogRef = useRef(null);
  const titleId = useId();
  useModalA11y({ isOpen, onClose, dialogRef });

  if (!isOpen) return null;

  return (
    <dialog ref={dialogRef} className="kc-sheet" aria-labelledby={titleId}>
      <div className="kc-sheet-panel">
        <span className="kc-sheet-grabber" aria-hidden="true" />
        <div className="kc-sheet-head">
          <h3 className="kc-sheet-title" id={titleId}>
            {title}
          </h3>
          <IconButton
            type={ICON_BUTTON_TYPES.BLUE}
            icon={ICON_BUTTON_ICONS.X_LG}
            withFill={false}
            withBorder={false}
            tooltipText="Close"
            wrapClassName="kc-sheet-close"
            onClick={onClose}
          />
        </div>
        <div className="kc-sheet-body">{children}</div>
        {footer ? <div className="kc-sheet-foot">{footer}</div> : null}
      </div>
    </dialog>
  );
};

KeyContactsMobileSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  footer: PropTypes.node
};

export default KeyContactsMobileSheet;
